import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import Button from 'ui-kit/Button';
import './Walkthrough.scss';

export interface WalkthroughStep {
  id: string;
  target: string | string[];
  title?: React.ReactNode;
  content: React.ReactNode;
  ctaLabel?: string;
}

export type WalkthroughFinishReason = 'completed' | 'skipped' | 'unavailable';

interface Props {
  steps: WalkthroughStep[];
  open: boolean;
  label?: string;
  celebrate?: boolean;
  onStepView?: (step: WalkthroughStep) => void;
  onFinish: (reason: WalkthroughFinishReason, step: WalkthroughStep) => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface Position {
  top: number;
  left: number;
  arrowLeft: number;
  placement: 'top' | 'bottom';
}

const BASE_CLASS = 'ui-kit-walkthrough';
const SPOTLIGHT_PADDING = 12;
const POPOVER_GAP = 16;
const VIEWPORT_MARGIN = 16;
const ARROW_INSET = 24;
const TARGET_TIMEOUT_MS = 5000;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const CELEBRATION_MS = 1400;
const SPARKLE_COUNT = 36;

const SPARKLES = Array.from({ length: SPARKLE_COUNT }, (_, index) => ({
  angle: (360 / SPARKLE_COUNT) * index + ((index * 47) % 20),
  distance: 90 + ((index * 53) % 110),
  delay: (index % 7) * 45,
  size: 8 + ((index * 31) % 12),
}));

// Responsive layouts render the same control twice and hide one, so the first match can be invisible
const findVisible = (selector: string) =>
  Array.from(document.querySelectorAll(selector)).find(
    (element) => element.getClientRects().length > 0,
  );

function useTargetRect(selectors: string[] | null) {
  const key = selectors ? selectors.join(',') : null;
  const [found, setFound] = useState<{ key: string | null; rect: Rect | null }>({
    key: null,
    rect: null,
  });
  const [missingKey, setMissingKey] = useState<string | null>(null);

  // Cleared on every key change: Back onto an auto-skipped step must search for its target again
  useEffect(() => setMissingKey(null), [key]);

  useEffect(() => {
    if (!selectors) {
      return undefined;
    }

    let elements: Element[] = [];
    let frame = 0;
    let observer: ResizeObserver | null = null;
    const startedAt = performance.now();

    const measure = () => {
      if (!elements.length) {
        return;
      }
      const rects = elements.map((element) => element.getBoundingClientRect());
      const top = Math.min(...rects.map((rect) => rect.top));
      const left = Math.min(...rects.map((rect) => rect.left));
      const right = Math.max(...rects.map((rect) => rect.right));
      const bottom = Math.max(...rects.map((rect) => rect.bottom));
      setFound({ key, rect: { top, left, width: right - left, height: bottom - top } });
    };

    // Targets can mount after the walkthrough does, e.g. behind a data fetch
    const find = () => {
      const found = selectors.map(findVisible);
      if (found.every(Boolean)) {
        elements = found;
        elements[0].scrollIntoView({ block: 'center' });
        measure();
        observer = new ResizeObserver(measure);
        elements.forEach((element) => observer.observe(element));
        return;
      }
      if (performance.now() - startedAt < TARGET_TIMEOUT_MS) {
        frame = requestAnimationFrame(find);
      } else {
        setMissingKey(key);
      }
    };
    find();

    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [key]);

  // Resetting these inside the effect lands a render late, so a step change would still read the
  // previous step's rect and fire onStepView against it. Matching on key discards it synchronously.
  return {
    rect: found.key === key ? found.rect : null,
    missing: key !== null && missingKey === key,
  };
}

export default function Walkthrough({
  steps,
  open,
  label = 'Walkthrough',
  celebrate = false,
  onStepView,
  onFinish,
}: Props) {
  const [index, setIndex] = useState(0);
  const [position, setPosition] = useState<Position | null>(null);
  const [celebrating, setCelebrating] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const viewedStepRef = useRef<string | null>(null);

  const step = open ? steps[index] : undefined;
  const isLast = index === steps.length - 1;
  const targets = step ? (Array.isArray(step.target) ? step.target : [step.target]) : null;
  const { rect, missing } = useTargetRect(targets);

  useEffect(() => {
    if (open) {
      setIndex(0);
      setCelebrating(false);
      viewedStepRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!celebrating) {
      return undefined;
    }
    const timeout = window.setTimeout(() => onFinish('completed', step), CELEBRATION_MS);
    return () => window.clearTimeout(timeout);
  }, [celebrating]);

  const next = useCallback(() => {
    if (!isLast) {
      setIndex((current) => current + 1);
    } else if (celebrate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setCelebrating(true);
    } else {
      onFinish('completed', step);
    }
  }, [isLast, celebrate, onFinish, step]);

  const back = useCallback(() => setIndex((current) => Math.max(current - 1, 0)), []);

  const skip = useCallback(() => onFinish('skipped', step), [onFinish, step]);

  useEffect(() => {
    if (!missing) {
      return;
    }
    if (isLast) {
      onFinish('unavailable', step);
    } else {
      setIndex((current) => current + 1);
    }
  }, [missing]);

  useLayoutEffect(() => {
    if (!rect || !popoverRef.current) {
      setPosition(null);
      return;
    }
    const { offsetWidth: width, offsetHeight: height } = popoverRef.current;
    const targetCenter = rect.left + rect.width / 2;
    const maxLeft = window.innerWidth - VIEWPORT_MARGIN - width;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(targetCenter - width / 2, maxLeft));
    const below = rect.top + rect.height + SPOTLIGHT_PADDING + POPOVER_GAP;
    const above = rect.top - SPOTLIGHT_PADDING - POPOVER_GAP - height;
    const fitsBelow = below + height <= window.innerHeight - VIEWPORT_MARGIN;
    const placement = !fitsBelow && above >= VIEWPORT_MARGIN ? 'top' : 'bottom';
    const maxTop = window.innerHeight - VIEWPORT_MARGIN - height;
    setPosition({
      top: Math.max(VIEWPORT_MARGIN, Math.min(placement === 'top' ? above : below, maxTop)),
      left,
      arrowLeft: Math.max(ARROW_INSET, Math.min(targetCenter - left, width - ARROW_INSET)),
      placement,
    });
  }, [rect]);

  useEffect(() => {
    if (!step || !rect || !position || viewedStepRef.current === step.id) {
      return;
    }
    viewedStepRef.current = step.id;
    popoverRef.current?.focus({ preventScroll: true });
    onStepView?.(step);
  }, [step, rect, position]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (!celebrating) {
          skip();
        }
        return;
      }
      if (event.key !== 'Tab' || !popoverRef.current) {
        return;
      }
      const popover = popoverRef.current;
      const focusable = Array.from(
        popover.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => element.getClientRects().length > 0);
      const first = focusable[0] ?? popover;
      const last = focusable[focusable.length - 1] ?? popover;
      const active = document.activeElement;

      if (!popover.contains(active)) {
        event.preventDefault();
        first.focus();
        return;
      }
      if (active === popover) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey ? active === first : active === last) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, celebrating, skip]);

  if (!step) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={clsx(BASE_CLASS, { [`${BASE_CLASS}--celebrating`]: celebrating })}
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div className={`${BASE_CLASS}__blocker`} />
      {rect && (
        <div
          className={`${BASE_CLASS}__spotlight`}
          style={{
            top: rect.top - SPOTLIGHT_PADDING,
            left: rect.left - SPOTLIGHT_PADDING,
            width: rect.width + SPOTLIGHT_PADDING * 2,
            height: rect.height + SPOTLIGHT_PADDING * 2,
          }}
        />
      )}
      {celebrating && rect && (
        <div
          className={`${BASE_CLASS}__sparkles`}
          style={{ top: rect.top + rect.height / 2, left: rect.left + rect.width / 2 }}
          aria-hidden="true"
        >
          {SPARKLES.map(({ angle, distance, delay, size }, sparkleIndex) => (
            <span
              key={sparkleIndex}
              className={`${BASE_CLASS}__sparkle`}
              style={
                {
                  '--angle': `${angle}deg`,
                  '--distance': `${distance}px`,
                  '--delay': `${delay}ms`,
                  '--size': `${size}px`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}
      <div
        ref={popoverRef}
        tabIndex={-1}
        className={clsx(`${BASE_CLASS}__popover`, {
          [`${BASE_CLASS}__popover--visible`]: position && !celebrating,
          [`${BASE_CLASS}__popover--top`]: position?.placement === 'top',
        })}
        style={position ? { top: position.top, left: position.left } : undefined}
      >
        <span
          className={`${BASE_CLASS}__arrow`}
          style={position ? { left: position.arrowLeft } : undefined}
        />
        <div className={`${BASE_CLASS}__content`}>
          {step.title && <p className={`${BASE_CLASS}__title`}>{step.title}</p>}
          {step.content}
        </div>
        <div className={`${BASE_CLASS}__footer`}>
          <div
            className={`${BASE_CLASS}__progress`}
            aria-label={`Step ${index + 1} of ${steps.length}`}
          >
            {steps.map(({ id }, stepIndex) => (
              <span
                key={id}
                className={clsx(`${BASE_CLASS}__dash`, {
                  [`${BASE_CLASS}__dash--active`]: stepIndex <= index,
                })}
              />
            ))}
          </div>
          {!isLast && (
            <Button variant="text" color="textGray" size="small" onClick={skip}>
              Skip
            </Button>
          )}
          {index > 0 && (
            <Button variant="outlined" size="small" onClick={back} disabled={celebrating}>
              Back
            </Button>
          )}
          <Button variant="contained" size="small" onClick={next} disabled={celebrating}>
            {step.ctaLabel ?? (isLast ? 'Done' : 'Next')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
